import { Router } from '@angular/router';

const NON_CLICKABLE_LOAD_STATUSES = new Set(['PLANNING', 'PENDING']);

const CONTROL_TOWER_LOAD_STATUSES = new Set([
  'SR_ACCEPTED',
  'RFQ_AWARDED',
  'SCHEDULE_NOTIFIED',
  'IN_PROGRESS',
  'COMPLETED',
  'TERMINATED',
]);

const SCHEDULING_LOAD_STATUSES = new Set(['SCHEDULED']);

export function normalizeLoadMasterStatusForNavigation(status?: string | null): string {
  const normalizedStatus = (status || '').toUpperCase().trim();
  if (normalizedStatus === 'TERMINATE') {
    return 'TERMINATED';
  }
  if (normalizedStatus === 'INPROGRESS') {
    return 'IN_PROGRESS';
  }
  return normalizedStatus;
}

export function isJobFileUrlVersionV2(urlVersion?: string | null): boolean {
  return (urlVersion ?? '').toLowerCase() === 'v2';
}

export interface NavigateByLoadMasterStatusOptions {
  rawStatus?: string | null;
  /** Primary job ref (e.g. load’s jobRefId) — used for control tower and as fallback */
  jobRefId: string;
  containerId?: string | null;
  jpmId?: string | null;
  navigateForPlanningOrPending: boolean;
  /** Per-document urlVersion from job master: v2 → schedule-v2 / load-assignment; else → schedule / wizard */
  urlVersion?: string | null;
  /**
   * Job-master row aggregate id (e.g. jobFile._id) for schedule + planning routes, matching legacy navigateToLoad.
   * When omitted, `jobRefId` is used everywhere.
   */
  aggregateJobRefId?: string | null;
}

function navigatePlanningPendingOrUnknownFallback(router: Router, opts: NavigateByLoadMasterStatusOptions): void {
  const planningRef = opts.aggregateJobRefId ?? opts.jobRefId;
  if (isJobFileUrlVersionV2(opts.urlVersion)) {
    router.navigate(['/load-management'], { queryParams: { jobRefId: planningRef } });
    return;
  }
  if (opts.containerId) {
    router.navigate(['/wizard/container-management'], {
      queryParams: { jobRefId: planningRef, order: 0, containerId: opts.containerId },
    });
  } else {
    router.navigate(['/load-management'], { queryParams: { jobRefId: planningRef } });
  }
}

export function navigateByLoadMasterStatus(router: Router, opts: NavigateByLoadMasterStatusOptions): void {
  if (!opts.jobRefId) {
    return;
  }

  const normalized = normalizeLoadMasterStatusForNavigation(opts.rawStatus);
  const scheduleRef = opts.aggregateJobRefId ?? opts.jobRefId;

  if (NON_CLICKABLE_LOAD_STATUSES.has(normalized)) {
    if (opts.navigateForPlanningOrPending) {
      navigatePlanningPendingOrUnknownFallback(router, opts);
    }
    return;
  }

  if (CONTROL_TOWER_LOAD_STATUSES.has(normalized)) {
    const queryParams: Record<string, string | boolean> = {
      jobRefId: opts.jobRefId,
      containerId: opts.containerId ?? ''
    };

    if (isJobFileUrlVersionV2(opts.urlVersion)) {
      queryParams['bookingEngine'] = true;
    } else {
      queryParams['bookingEngine'] = false;
    }
    if (opts.jpmId) {
      queryParams['jpmId'] = opts.jpmId;
    }
    router.navigate(['/control-tower'], { queryParams });
    return;
  }

  if (SCHEDULING_LOAD_STATUSES.has(normalized)) {
    if (isJobFileUrlVersionV2(opts.urlVersion)) {
      router.navigate(['/schedule-v2'], { queryParams: { jobRefId: scheduleRef } });
    } else {
      router.navigate(['/schedule'], { queryParams: { jobRefId: scheduleRef } });
    }
    return;
  }

  if (opts.navigateForPlanningOrPending) {
    navigatePlanningPendingOrUnknownFallback(router, opts);
  }
}
